import { Col, Row, Space } from "antd";
import { useLocation } from "react-router-dom";

import CatalogIcon from "@/components/icons/chapters/CatalogIcon";
import EmployeesIcon from "@/components/icons/chapters/EmployeesIcon";
import FacilitiesIcon from "@/components/icons/chapters/FacilitiesIcon";
import FullCompanyDashboardIcon from "@/components/icons/chapters/FullCompanyDashboardIcon";
import ImpactReferencesIcon from "@/components/icons/chapters/ImpactReferencesIcon";
import OrdersIcon from "@/components/icons/chapters/OrdersIcon";
import OverviewIcon from "@/components/icons/chapters/OverviewIcon";
import PackageIcon from "@/components/icons/chapters/PackageIcon";
import ProductsDashboardIcon from "@/components/icons/chapters/ProductsDashboardIcon";
import TransportIcon from "@/components/icons/chapters/TransportIcon";
import LinkOrSpan from "@/components/links/LinkOrSpan/LinkOrSpan";
import PremiumBannerTooltip from "@/components/PremiumFeatureComponents/PremiumBannerTooltip";
import PremiumSidebar from "@/components/PremiumFeatureComponents/PremiumSidebar";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { useDashboardImpactMetricKey } from "@/pages/dashboard/hooks/useImpactMetric";
import {
  ApiOutlined,
  BlockOutlined,
  BuildOutlined,
  DatabaseOutlined,
  FileAddOutlined,
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Fragment, ReactNode, useEffect } from "react";
import useLogout from "../ProfileMenu/useLogout";
import styles from "./RootMenu.module.scss";

export interface MenuItem {
  key: string;
  label: string;
  url?: string;
  active?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
  showPremiumBanner?: boolean;
  hidden?: boolean;
  onClick?: () => void;
}

export type MenuCategory = "collecting" | "matching" | "impact" | "settings" | "admin";

export interface MenuParentItem extends MenuItem {
  loading: boolean;
  children?: MenuItem[];
  mainCategory: MenuCategory;
}

export function useMenu() {
  const location = useLocation();
  const { data: companySettings } = useCompanySettings();
  const metricKey = useDashboardImpactMetricKey();
  const includeEol = companySettings?.include_eol || false;
  const includeUsage = companySettings?.include_usage || false;
  const { logout } = useLogout();

  const menuItems: MenuParentItem[] = [
    {
      key: "overview",
      label: "Overview",
      disabled: false,
      loading: false,
      url: "/",
      active: location.pathname === "/",
      icon: <OverviewIcon />,
      mainCategory: "collecting",
    },
    {
      key: "orders",
      label: "Orders",
      disabled: false,
      loading: false,
      icon: <OrdersIcon />,
      children: [
        {
          key: "purchase",
          label: "Purchase",
          url: "/orders/purchase_orders",
          active: location.pathname.startsWith(`/orders/purchase_orders`),
        },
        {
          key: "sales",
          label: "Sales",
          url: "/orders/sales_orders",
          active: location.pathname.startsWith(`/orders/sales_orders`),
        },
      ],
      mainCategory: "collecting",
    },
    {
      key: "catalog",
      label: "Products",
      disabled: false,
      loading: false,
      icon: <CatalogIcon />,
      url: "/products",
      active:
        location.pathname.startsWith("/products") &&
        location.pathname !== "/products/bom" &&
        location.pathname !== "/products/usage" &&
        location.pathname !== "/products/eol",
      children: [
        {
          key: "boms",
          label: "Bill of materials",
          url: "/products/bom",
          active: location.pathname === `/products/bom`,
        },
        {
          key: "usage",
          label: "Usage",
          url: "/products/usage",
          hidden: !includeUsage,
          active: location.pathname === "/products/usage",
        },
        {
          key: "eol",
          label: "End of life",
          url: "/products/eol",
          hidden: !includeEol,
          active: location.pathname === "/products/eol",
        },
      ],
      mainCategory: "collecting",
    },
    {
      key: "packaging",
      label: "Product packaging",
      disabled: false,
      loading: false,
      url: "/packaging",
      active: location.pathname.startsWith(`/packaging`),
      icon: (
        <PackageIcon
          width={15}
          height={15}
        />
      ),
      mainCategory: "collecting",
    },
    {
      key: "facilities",
      label: "Facilities",
      disabled: false,
      loading: false,
      url: "/facilities",
      active: location.pathname.startsWith(`/facilities`),
      icon: <FacilitiesIcon />,
      mainCategory: "collecting",
    },
    {
      key: "transport",
      label: "Transport",
      disabled: false,
      loading: false,
      icon: <TransportIcon />,
      children: [
        {
          key: "inbound",
          label: "Inbound",
          url: "/transport/inbound",
          active: location.pathname.startsWith(`/transport/inbound`),
        },
        {
          key: "outbound",
          label: "Outbound",
          url: "/transport/outbound",
          active: location.pathname.startsWith(`/transport/outbound`),
        },
      ],
      mainCategory: "collecting",
    },
    {
      key: "employees",
      label: "Employees",
      disabled: false,
      loading: false,
      icon: <EmployeesIcon />,
      children: [
        {
          key: "commute",
          label: "Commute",
          url: "/employees/commute",
          active: location.pathname.startsWith(`/employees/commute`),
        },
        {
          key: "travel",
          label: "Business travel",
          url: "/employees/business_travel",
          active: location.pathname.startsWith(`/employees/business_travel`),
        },
      ],
      mainCategory: "collecting",
    },
    {
      key: "matching_overview",
      label: "Overview",
      disabled: false,
      icon: <OverviewIcon />,
      loading: false,
      url: "/matching",
      active: location.pathname === `/matching`,
      mainCategory: "matching",
    },
    {
      key: "matching",
      label: "Matching",
      disabled: false,
      icon: <ApiOutlined />,
      loading: false,
      children: [
        {
          key: "materials",
          label: "Materials",
          url: "/matching/materials",
          active: location.pathname.startsWith(`/matching/materials`),
        },
        {
          key: "packaging_materials",
          label: "Packaging materials",
          url: "/matching/packaging/materials",
          active: location.pathname.startsWith(`/matching/packaging/materials`),
        },
        {
          key: "consumables",
          label: "Consumables",
          url: "/consumables",
          active: location.pathname.startsWith(`/consumables`),
        },
        {
          key: "goods_and_services",
          label: "Goods & services",
          url: "/goods_and_services",
          active: location.pathname.startsWith(`/goods_and_services`),
        },
        {
          key: "travel_modes",
          label: "Travel modes",
          url: "/employees/travel_modes",
          active: location.pathname.startsWith(`/employees/travel_modes`),
        },
      ],
      mainCategory: "matching",
    },
    {
      key: "references",
      label: "References",
      disabled: false,
      loading: false,
      icon: <ImpactReferencesIcon />,
      mainCategory: "matching",
      url: "/impact_references",
      active: location.pathname === `/impact_references`,
      children: [
        {
          key: "impact_references_eco",
          label: "EcoInvent",
          url: "/impact_references/eco_invent",
          active: location.pathname.startsWith(`/impact_references/eco_invent`),
        },
        {
          key: "impact_references_agrifootprint",
          label: "Agri-footprint",
          url: "/impact_references/agri_footprint",
          active: location.pathname.startsWith(`/impact_references/agri_footprint`),
        },
        {
          key: "impact_references_root",
          label: "Root",
          url: "/impact_references/root",
          active: location.pathname.startsWith(`/impact_references/root`),
        },
        {
          key: "impact_references_epd",
          label: "EPD",
          url: "/impact_references/epd",
          active: location.pathname.startsWith(`/impact_references/epd`),
        },
        {
          key: "impact_references_custom",
          label: "Custom",
          url: "/impact_references/custom",
          active: location.pathname.startsWith(`/impact_references/custom`),
        },
      ],
    },
    {
      key: "account",
      label: "Account",
      disabled: false,
      loading: false,
      url: "/account",
      active: location.pathname.startsWith("/account"),
      icon: <UserOutlined />,
      mainCategory: "settings",
    },
    {
      key: "team",
      label: "Team",
      disabled: false,
      loading: false,
      url: "/team",
      active: location.pathname.startsWith("/team"),
      icon: <TeamOutlined />,
      mainCategory: "settings",
    },
    {
      key: "settings",
      label: "Settings",
      disabled: false,
      loading: false,
      url: "/settings",
      active: location.pathname.startsWith("/settings"),
      icon: <SettingOutlined />,
      mainCategory: "settings",
    },
    {
      key: "snapshots",
      label: "Snapshots",
      disabled: false,
      icon: <DatabaseOutlined />,
      loading: false,
      url: "/snapshots",
      active: location.pathname.startsWith("/snapshots"),
      mainCategory: "settings",
    },
    {
      key: "logout",
      label: "Logout",
      disabled: false,
      icon: <LogoutOutlined />,
      loading: false,
      onClick: () => logout(),
      active: location.pathname.startsWith("/snapshots"),
      mainCategory: "settings",
    },
    {
      key: "users",
      label: "Users",
      disabled: false,
      loading: false,
      url: "/admin/users",
      active: location.pathname.startsWith("/admin/users"),
      icon: <UserOutlined />,
      mainCategory: "admin",
    },
    {
      key: "companies",
      label: "Companies",
      disabled: false,
      icon: <BuildOutlined />,
      loading: false,
      url: "/admin/companies",
      active: location.pathname.startsWith("/admin/companies"),
      mainCategory: "admin",
    },
    {
      key: "root_impact_references",
      label: "Impact references",
      disabled: false,
      icon: <BlockOutlined />,
      loading: false,
      url: "/admin/root_impact_references",
      active: location.pathname.startsWith("/admin/root_impact_references"),
      mainCategory: "admin",
    },
    {
      key: "corporate_footprint",
      label: "Corporate footprint",
      showPremiumBanner: !companySettings?.access.includes("lca"),
      loading: false,
      icon: <FullCompanyDashboardIcon />,
      url: `/dashboard/corporate/${metricKey}`,
      active:
        location.pathname.startsWith(`/dashboard/corporate`) || location.pathname === `/dashboard`,
      children: [
        {
          key: "dashboard_materials",
          url: `/dashboard/materials/${metricKey}`,
          active: location.pathname.startsWith(`/dashboard/materials`),
          label: "Materials",
        },
        {
          key: "dashboard_packaging",
          url: `/dashboard/packaging/${metricKey}`,
          active: location.pathname.startsWith(`/dashboard/packaging`),
          label: "Packaging",
        },
        {
          key: "dashboard_facilities",
          url: `/dashboard/facility/${metricKey}`,
          active: location.pathname.startsWith(`/dashboard/facility`),
          label: "Facilities",
        },
        {
          key: "dashboard_transport",
          url: `/dashboard/transport/${metricKey}`,
          active: location.pathname.startsWith(`/dashboard/transport`),
          label: "Transport",
        },
        {
          key: "dashboard_customer",
          url: `/dashboard/customer/${metricKey}`,
          active: location.pathname.startsWith(`/dashboard/customer`),
          label: "Customer",
        },
        {
          key: "dashboard_employees",
          url: `/dashboard/employee/${metricKey}`,
          active: location.pathname.startsWith(`/dashboard/employee`),
          label: "Employee",
        },
      ],
      disabled: !companySettings?.access.includes("lca"),
      mainCategory: "impact",
    },
    {
      key: "dashboard_products",
      label: "Product footprint",
      loading: false,
      disabled: !companySettings?.access.includes("products"),
      showPremiumBanner: !companySettings?.access.includes("products"),
      url: `/dashboard/products/${metricKey}`,
      active: location.pathname.startsWith(`/dashboard/products`),
      icon: <ProductsDashboardIcon />,
      mainCategory: "impact",
    },
    {
      key: "dashboard_ghg",
      url: `/dashboard/ghg`,
      loading: false,
      disabled: !companySettings?.access.includes("ghg"),
      showPremiumBanner: !companySettings?.access.includes("ghg"),
      active: location.pathname.startsWith(`/dashboard/ghg`),
      label: "GHG report",
      icon: <FileAddOutlined />,
      mainCategory: "impact",
    },
  ];

  const findCurrentPageAndCategory = (): {
    page?: MenuItem;
    category: MenuCategory;
  } => {
    for (let i = 0; i < menuItems.length; i++) {
      const menuItem = menuItems[i];
      if (menuItem.active) return { page: menuItem, category: menuItem.mainCategory };
      if (menuItem.children) {
        const activeChild = menuItem.children.find((child) => child.active);
        if (activeChild) return { page: activeChild, category: menuItem.mainCategory };
      }
    }
    return { page: undefined, category: "collecting" as MenuCategory };
  };

  const { page, category } = findCurrentPageAndCategory();

  return { menuItems, page, category };
}

export default function RootMenu() {
  const { menuItems, page, category } = useMenu();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className={styles.RootMenu}>
      <div className={styles.menu}>
        <div className={styles.MenuMakerContainer}>
          {menuItems
            .filter((menuItem) => menuItem.mainCategory === category)
            .map((menuHeader) => (
              <Fragment key={menuHeader.key}>
                {menuHeader.label && (
                  <PremiumBannerTooltip
                    show={!!menuHeader.showPremiumBanner}
                    placement="right"
                  >
                    <LinkOrSpan
                      onClick={menuHeader.onClick}
                      to={menuHeader.disabled ? undefined : menuHeader.url}
                    >
                      <Row
                        align="middle"
                        className={`${styles.mainLabelContainer} ${
                          menuHeader.key === page?.key && styles.labelActive
                        }  ${menuHeader.disabled ? styles.disabled : ""} ${
                          (menuHeader.children?.length || 0) > 0 && !menuHeader.url
                            ? styles.noHover
                            : ""
                        }`}
                      >
                        <Col
                          span={4}
                          className={styles.iconContainer}
                        >
                          {menuHeader.icon}
                        </Col>
                        <Col
                          span={20}
                          className={`${styles.mainLabelContent}`}
                        >
                          <Space>{menuHeader.label}</Space>
                        </Col>
                      </Row>
                    </LinkOrSpan>
                  </PremiumBannerTooltip>
                )}
                {menuHeader.children && (
                  <Row className={`${styles.subMenu} ${styles.subMenuOpen}`}>
                    <Col
                      span={4}
                      className={styles.subMenuLeftContainer}
                    >
                      <div className={styles.subMenuLeftLine}></div>
                    </Col>
                    <Col span={20}>
                      <Row>
                        {menuHeader.children.map(
                          (menuItem) =>
                            !menuItem.hidden && (
                              <Col
                                key={menuItem.label}
                                span={24}
                              >
                                <LinkOrSpan
                                  onClick={menuItem.onClick}
                                  to={menuItem.disabled ? undefined : menuItem.url}
                                  className={`${styles.subMenuContent} ${
                                    menuItem.key === page?.key && styles.labelActive
                                  } ${styles.mainLabelContent} ${
                                    menuItem.disabled ? styles.disabled : ""
                                  }`}
                                >
                                  <PremiumBannerTooltip
                                    show={!!menuItem.showPremiumBanner}
                                    placement={"right"}
                                  >
                                    <span>{menuItem.label}</span>
                                  </PremiumBannerTooltip>
                                </LinkOrSpan>
                              </Col>
                            )
                        )}
                      </Row>
                    </Col>
                  </Row>
                )}
              </Fragment>
            ))}
        </div>
        <div className={styles.premiumFeature}>
          <PremiumSidebar />
        </div>
      </div>
    </div>
  );
}
